// Leitura da "análise breve" do Dwight (Haiku) — compartilhada pelo
// ModalDecisao e pelo histórico de análises do ModalLicitacao.
//
// Por que existe (2026-09-02): o parse defensivo do JSON e o mapa de
// recomendação moravam só no ModalDecisao; o ModalLicitacao mostrava o
// `conteudo` cru (um JSON de vários KB) no histórico. Agora os dois leem
// pelo mesmo caminho.

export interface AnaliseParsed {
  objeto_detalhado?: string;
  valor_estimado?: number | null;
  prazo_execucao?: string;
  modalidade?: string;
  data_abertura?: string;
  local_servico?: string;
  visita_tecnica?: string | { obrigatoria?: boolean; data?: string; observacao?: string };
  qualificacao_tecnica_resumo?: string;
  qualificacao_financeira_resumo?: string;
  // O prompt pede uma LISTA aqui ("lista das CNDs e documentos exigidos"),
  // mas o modelo às vezes devolve um parágrafo. Nenhuma tela renderiza este
  // campo hoje; o tipo fica honesto para quem for renderizar não escrever
  // `{obj}` dentro do JSX e derrubar a tela.
  documentos_juridico_fiscais?: string | string[];
  fit_tecnico_hydrodata?: { nivel?: string; justificativa?: string } | string;
  riscos_atencao?: string | string[];
  recomendacao?: string;  // "vale_aprofundar" | "atencao" | "nao_recomendado"
  resumo_executivo_md?: string;
}

/**
 * Parse defensivo (L15.9, 2026-06-03): aceita JSON puro ou envolvido em
 * ```json ... ``` — o modelo às vezes envelopa. Devolve null se não parsear;
 * quem chama decide mostrar o texto cru.
 */
export function parseAnaliseBreve(conteudo: string | null | undefined): AnaliseParsed | null {
  if (!conteudo) return null;
  try {
    const semWrapper = String(conteudo)
      .replace(/^\s*```(?:json|JSON)?\s*/i, '')
      .replace(/\s*```\s*$/, '')
      .trim();
    const m = semWrapper.match(/\{[\s\S]*\}/);
    const obj = JSON.parse(m ? m[0] : semWrapper);
    return obj && typeof obj === 'object' ? obj as AnaliseParsed : null;
  } catch {
    return null;
  }
}

export type IntentRecomendacao = 'gerar' | 'neutro' | 'descartar';

export interface RecomendacaoUI {
  label: string;
  /** Cor hex (texto/ícone). */
  color: string;
  /** Cor de Tag do antd equivalente. */
  tagColor: string;
  intent: IntentRecomendacao;
  explicacao: string;
}

/** Recomendação do modelo → rótulo, cores e intenção (descartar / gerar / neutro). */
export function recomendacaoUI(rec?: string | null): RecomendacaoUI {
  switch ((rec || '').toLowerCase()) {
    case 'vale_aprofundar':
      return {
        label: 'Vale aprofundar', color: '#0a7f4a', tagColor: 'green', intent: 'gerar',
        explicacao: 'Dwight identificou bom fit. Recomendação: seguir pra proposta.',
      };
    case 'atencao':
      return {
        label: 'Atenção', color: '#B07500', tagColor: 'orange', intent: 'neutro',
        explicacao: 'Há pontos a avaliar antes de decidir. Leia a síntese.',
      };
    case 'nao_recomendado':
      return {
        label: 'Não recomendado', color: '#B91626', tagColor: 'red', intent: 'descartar',
        explicacao: 'Dwight aponta motivos pra descartar. Veja os riscos abaixo.',
      };
    default:
      return {
        label: rec || 'Sem recomendação', color: '#888', tagColor: 'default', intent: 'neutro',
        explicacao: 'Recomendação não classificada pelo Dwight.',
      };
  }
}

/**
 * Nível do fit técnico (alto/médio/baixo) → cor e rótulo.
 * O prompt pede "medio" sem acento, mas o modelo escreve "médio" quando lhe
 * dá na telha — e aí a tag saía cinza com o texto cru. Normaliza o acento
 * antes de comparar (a palavra em si continua aparecendo no fallback).
 */
export function nivelFitUI(nivel?: string | null): { color: string; tagColor: string; label: string } {
  const chave = (nivel || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
  switch (chave) {
    case 'alto':  return { color: '#0a7f4a', tagColor: 'green',  label: 'Alto' };
    case 'medio': return { color: '#B07500', tagColor: 'orange', label: 'Médio' };
    case 'baixo': return { color: '#B91626', tagColor: 'red',    label: 'Baixo' };
    default:      return { color: '#888',    tagColor: 'default', label: nivel || '—' };
  }
}

/** `fit_tecnico_hydrodata` pode vir objeto ou string — normaliza. */
export function fitTecnicoDaAnalise(parsed: AnaliseParsed | null): { nivel?: string; justificativa?: string } | null {
  if (!parsed?.fit_tecnico_hydrodata) return null;
  const f = parsed.fit_tecnico_hydrodata;
  if (typeof f === 'string') return { justificativa: f };
  return f;
}
